import { createBrowserRouter, Navigate, Outlet, redirect, RouterProvider } from 'react-router-dom';

import AcceptedPlansPage from 'components/acceptedPlans/AcceptedPlansPage';
import Admissions from 'components/admin/admissions/Admissions';
import EditAdmissionPrompt from 'components/admin/admissions/EditAdmissionPrompt';
import EditAICriteriaSet from 'components/admin/admissions/EditAICriteriaSet';
import AIPrompts from 'components/admin/aiPrompts/AIPrompts';
import EditAIPrompt from 'components/admin/aiPrompts/EditAIPrompt';
import Attributes from 'components/admin/attributes/Attributes';
import EditAttribute from 'components/admin/attributes/EditAttribute';
import EditAttributeValue from 'components/admin/attributes/EditAttributeValue';
import BulkImportDetail from 'components/admin/bulkImports/BulkImportDetail';
import BulkImportHistory from 'components/admin/bulkImports/BulkImportHistory';
import BulkImportNew from 'components/admin/bulkImports/BulkImportNew';
import Classifications from 'components/admin/classifications/Classifications';
import AdminReferralSources from 'components/admin/clients/AdminReferralSources';
import Clients from 'components/admin/clients/Clients';
import ConfigureCareOptions from 'components/admin/clients/configureCareOptions/ConfigureCareOptions';
import EditClient from 'components/admin/clients/EditClient';
import EditImportConfiguration from 'components/admin/clients/importConfigurations/EditImportConfiguration';
import PortalAutomationSettings from 'components/admin/clients/portalAutomation/PortalAutomationSettings';
import ProductAccess from 'components/admin/clients/productAccess/ProductAccess';
import CoordinationRequests from 'components/admin/coordinationRequests/CoordinationRequests';
import DemoEnvSetup from 'components/admin/demoEnvSetup/DemoEnvSetup';
import DischargeReasons from 'components/admin/dischargeReasons/DischargeReasons';
import EditDischargeReason from 'components/admin/dischargeReasons/EditDischargeReason';
import EditFeatureFlag from 'components/admin/featureFlags/EditFeatureFlag';
import FeatureFlags from 'components/admin/featureFlags/FeatureFlags';
import EditGroup from 'components/admin/groups/EditGroup';
import Groups from 'components/admin/groups/Groups';
import EditGroupType from 'components/admin/groupTypes/EditGroupType';
import GroupTypes from 'components/admin/groupTypes/GroupTypes';
import ImportHistory from 'components/admin/importHistory/ImportHistory';
import ImportStatusDetails from 'components/admin/importHistory/ImportStatusDetails';
import LinkedRecords from 'components/admin/linkedRecords/LinkedRecords';
import EditLocation from 'components/admin/locations/EditLocation';
import Locations from 'components/admin/locations/Locations';
import QhinConfiguration from 'components/admin/locations/qhinConfiguration/QhinConfiguration';
import EditPortalSystem from 'components/admin/portalSystems/EditPortalSystem';
import PortalSystems from 'components/admin/portalSystems/PortalSystems';
import PortalProblemReports from 'components/admin/portalSystems/problemReports/PortalProblemReports';
import EditQuestionTemplate from 'components/admin/questionTemplates/EditQuestionTemplate';
import QuestionTemplates from 'components/admin/questionTemplates/QuestionTemplates';
import Summaries from 'components/admin/summaries/Summaries';
import AdminEditPermissions from 'components/admin/users/AdminEditPermissions';
import AdminUserPreferences from 'components/admin/users/AdminUserPreferences';
import EditUser from 'components/admin/users/EditUser';
import Users from 'components/admin/users/Users';
import WorkflowLookups from 'components/admin/workflowLookups/WorkflowLookups';
import RunDetail from 'components/admin/workflowRuns/RunDetail';
import RunHealth from 'components/admin/workflowRuns/RunHealth';
import WorkflowRuns from 'components/admin/workflowRuns/WorkflowRuns';
import EditWorkspacePrompt from 'components/admin/workspacePrompts/EditWorkspacePrompt';
import WorkspacePrompts from 'components/admin/workspacePrompts/WorkspacePrompts';
import AdmissionSlideout from 'components/admissions/AdmissionSlideout';
import AdmissionsPage from 'components/admissions/AdmissionsPage';
import AllPatientsPage from 'components/allPatients/AllPatients';
import AuthError from 'components/AuthError';
import CoordinateLayout from 'components/coordinate/CoordinateLayout';
import CoordinateRequestsPage from 'components/coordinate/requests/CoordinateRequestsPage';
import CoordinateIndex from 'components/coordinateRequest/CoordinateIndex';
import CoordinateRequestSubmittedPage from 'components/coordinateRequest/CoordinateRequestSubmittedPage';
import DenialListPage from 'components/denialList/DenialListPage';
import { DISCHARGE_CALENDAR_ROUTES, DISCHARGE_CALENDAR_VIEWS } from 'components/dischargeCalendar/constants';
import DayView from 'components/dischargeCalendar/dayView/DayView';
import DischargeCalendarPage from 'components/dischargeCalendar/DischargeCalendarPage';
import ListView from 'components/dischargeCalendar/listView/ListView';
import MonthView from 'components/dischargeCalendar/monthView/MonthView';
import TwoWeekView from 'components/dischargeCalendar/twoWeekView/TwoWeekView';
import WeekView from 'components/dischargeCalendar/weekView/WeekView';
import External from 'components/External';
import FacilityNetworkStatusPage from 'components/groupPayerNetworkStatuses/FacilityNetworkStatusPage';
import NetworkStatusPage from 'components/groupPayerNetworkStatuses/NetworkStatusPage';
import HighCostMedsPage from 'components/highCostMeds/HighCostMedsPage';
import { INBOUND_REFERRALS_ROUTES } from 'components/inboundReferrals/constants';
import EditInboundReferralPage from 'components/inboundReferrals/EditInboundReferralPage';
import InboundReferralPage from 'components/inboundReferrals/InboundReferralPage';
import InboundReferralsListPage from 'components/inboundReferrals/InboundReferralsListPage';
import TasksDeepLinkScopeGate from 'components/inboundReferrals/tasks/TasksDeepLinkScopeGate';
import TasksPage from 'components/inboundReferrals/tasks/TasksPage';
import WorkflowScopeGate from 'components/inboundReferrals/WorkflowScopeGate';
import BusinessDevelopmentContainer from 'components/insights/careTransitions/businessDevelopment/BusinessDevelopmentContainer';
import InboundReferralsInsightsPage from 'components/insights/careTransitions/inboundReferrals/InboundReferralsInsightsPage';
import NetworkPerformanceContainer from 'components/insights/careTransitions/networkPerformance/NetworkPerformanceContainer';
import ReferralsContainer from 'components/insights/careTransitions/referrals/ReferralsContainer';
import ReferralsDashboard from 'components/insights/careTransitions/referrals/ReferralsDashboard';
import { Routes as InsightsRoutes } from 'components/insights/constants';
import InsightsContainer from 'components/insights/InsightsContainer';
import ActivitiesPerPatientWeekDetail from 'components/insights/layout/details/activitiesPerPatientWeek/ActivitiesPerPatientWeekDetail';
import AverageLengthOfStayDetail from 'components/insights/layout/details/averageLengthOfStay/AverageLengthOfStayDetail';
import OlioEngagementScoreDetail from 'components/insights/layout/details/olioEngagementScore/OlioEngagementScoreDetail';
import ReadmissionRateDetail from 'components/insights/layout/details/readmissionRate/ReadmissionRateDetail';
import ResponseTimeDetail from 'components/insights/layout/details/responseTime/ResponseTimeDetail';
import TotalDischargesDetail from 'components/insights/layout/details/totalDischarges/TotalDischargesDetail';
import MetricsDashboard from 'components/insights/layout/MetricsDashboard';
import AddPatientPage from 'components/intake/AddPatientPage';
import CoordinatePatientPage from 'components/intake/CoordinatePatientPage';
import EditPatientPage from 'components/intake/EditPatientPage';
import MainLayout from 'components/layouts/MainLayout';
import LoggedOut from 'components/LoggedOut';
import UserNotificationPreferences from 'components/notificationPreferences/UserNotificationPreferences';
import ConcurrentReviewsPage from 'components/patient/concurrentReview/ConcurrentReviewsPage';
import FilesPage from 'components/patient/FilesPage';
import PatientPage from 'components/patient/Patient';
import PatientStoryPage from 'components/patient/PatientStoryPage';
import PortfolioPage from 'components/portfolio/Portfolio';
import PreferredProvidersPage from 'components/preferredProviders/PreferredProvidersPage';
import ReferralPage from 'components/referral/ReferralPage';
import ReferralsListPage from 'components/referrals/ReferralsListPage';
import ReferralSourcesPage from 'components/referralSources/ReferralSourcesPage';
import RootProviders from 'components/Root';
import HomeRedirect from 'components/router/HomeRedirect';
import PermissionRedirect from 'components/router/PermissionRedirect';
import ProtectedRoute from 'components/router/ProtectedRoute';
import NewSummarizationPage from 'components/summarization/NewSummarizationPage';
import ViewSummarizationPage from 'components/summarization/ViewSummarizationPage';
import SummarizationsListPage from 'components/summarizations/SummarizationsListPage';
import ThemeProvider from 'components/ThemeProvider';
import { Flags } from 'constants/flags';
import { EpisodeProvider } from 'context/episode';
import { SelectedLocationEpisodeProvider } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import { AuthorizationProvider } from 'context/review/authorization';
import { ReferralProvider } from 'context/review/referral';
import { useAdminNavigationTracker } from 'hooks/useAdminListPage';
import { EscalationTypes } from 'models/Escalation';
import { PERMISSIONS, PRODUCT_ACCESSORS } from 'models/Profile';

function AdminGuard() {
  const { profile } = useProfile();
  useAdminNavigationTracker();

  return (
    <ProtectedRoute deny={!profile.isAdmin}>
      <Outlet />
    </ProtectedRoute>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootProviders />,
    children: [
      {
        index: true,
        element: <HomeRedirect />,
      },
      {
        path: '/auth/callback',
        element: <></>,
      },
      {
        path: '/external',
        element: <External />,
      },
      {
        path: '/',
        element: <MainLayout />,
        children: [
          {
            element: (
              <ProtectedRoute accessor={PRODUCT_ACCESSORS.careCoordinationEnabled}>
                <Outlet />
              </ProtectedRoute>
            ),
            children: [
              {
                path: '/patients',
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: <PortfolioPage />,
                  },
                  {
                    path: 'new',
                    element: <AddPatientPage />,
                  },
                  {
                    path: ':episodeId/*',
                    element: (
                      <EpisodeProvider>
                        <SelectedLocationEpisodeProvider>
                          <ReferralProvider>
                            <AuthorizationProvider>
                              <Outlet />
                            </AuthorizationProvider>
                          </ReferralProvider>
                        </SelectedLocationEpisodeProvider>
                      </EpisodeProvider>
                    ),
                    children: [
                      {
                        path: '*',
                        element: <PatientPage />,
                        children: [
                          {
                            path: '*',
                            element: <PatientStoryPage />,
                            children: [
                              {
                                path: 'authorization/:authorizationId/*',
                                element: null,
                              },
                            ],
                          },
                          {
                            path: 'concurrent-reviews',
                            element: <ConcurrentReviewsPage />,
                            children: [
                              {
                                path: ':authorizationId/*',
                                element: null,
                              },
                            ],
                          },
                          {
                            path: 'referrals',
                            element: <ReferralPage />,
                            children: [
                              {
                                path: ':referralId/*',
                                element: null,
                              },
                            ],
                          },
                          {
                            path: 'files',
                            element: <FilesPage />,
                          },
                        ],
                      },
                      {
                        path: 'edit',
                        element: <EditPatientPage />,
                      },
                    ],
                  },
                ],
              },
              {
                path: '/all-patients',
                element: <AllPatientsPage />,
              },
              {
                // Kept as a redirect, unlike OLIO-2916's moves: those two pages were a day
                // old when their routes changed, whereas this one has been live since
                // 2025-08-20 and may be bookmarked. Without this, the catch-all at the bottom
                // of this file would silently land an old link on /patients.
                path: '/contracted-health-plans',
                loader: async () => redirect('/configuration/contracted-health-plans'),
              },
              {
                path: '/referrals',
                element: <ReferralsListPage />,
                children: [
                  {
                    path: ':referralId/*',
                    element: null,
                  },
                ],
              },
              {
                path: '/insights/referrals',
                element: <ReferralsContainer />,
                children: [
                  {
                    index: true,
                    element: <ReferralsDashboard />,
                  },
                ],
              },
              {
                path: '/insights/post-discharge',
                element: <NetworkPerformanceContainer />,
              },
              {
                path: '/insights/business-development',
                element: <BusinessDevelopmentContainer />,
              },
              {
                path: '/insights',
                element: <InsightsContainer />,
                children: [
                  {
                    index: true,
                    element: <MetricsDashboard />,
                  },
                  {
                    path: InsightsRoutes.OLIO_ENGAGEMENT_SCORE,
                    element: <OlioEngagementScoreDetail />,
                  },
                  {
                    path: InsightsRoutes.READMISSION_RATE,
                    element: <ReadmissionRateDetail />,
                  },
                  {
                    path: InsightsRoutes.AVERAGE_LENGTH_OF_STAY,
                    element: <AverageLengthOfStayDetail />,
                  },
                  {
                    path: InsightsRoutes.TOTAL_DISCHARGES,
                    element: <TotalDischargesDetail />,
                  },
                  {
                    path: InsightsRoutes.ACTIVITIES_PER_PATIENT_PER_WEEK,
                    element: <ActivitiesPerPatientWeekDetail />,
                  },
                  {
                    path: InsightsRoutes.PRIORITIES_NOTE_RESPONSE,
                    element: <ResponseTimeDetail escalationType={EscalationTypes.PriorityNote} />,
                  },
                  {
                    path: InsightsRoutes.ESCALATIONS_RESPONSE,
                    element: <ResponseTimeDetail escalationType={EscalationTypes.Escalation} />,
                  },
                  {
                    path: '*',
                    loader: async () => redirect('/insights'),
                  },
                ],
              },
              {
                path: '/coordinate',
                element: <CoordinateLayout />,
                children: [
                  { index: true, element: <CoordinateIndex /> },
                  { path: 'submitted', element: <CoordinateRequestSubmittedPage /> },
                  { path: 'requests', element: <CoordinateRequestsPage /> },
                  { path: ':id', element: <CoordinatePatientPage /> },
                ],
              },
              {
                path: '/connect-patients',
                loader: async () => redirect('/coordinate'),
              },
              {
                path: '/preferences',
                element: <UserNotificationPreferences />,
              },
            ],
          },
          {
            path: '/admissions',
            element: (
              <ProtectedRoute accessor={PRODUCT_ACCESSORS.admissionsManagementEnabled}>
                <AdmissionsPage />
              </ProtectedRoute>
            ),
            children: [
              {
                path: ':admissionId',
                element: <AdmissionSlideout />,
                children: [
                  {
                    index: true,
                    loader: ({ params }) => redirect(`/admissions/${params.admissionId}/criteria`),
                  },
                  { path: 'criteria', element: null },
                  { path: 'summary', element: null },
                  { path: 'files', element: null },
                ],
              },
            ],
          },
          {
            path: '/configuration',
            children: [
              {
                // No landing page; send a bare /configuration where the catch-all would.
                index: true,
                loader: async () => redirect('/patients'),
              },
              {
                path: 'contracted-health-plans',
                element: (
                  // Care Coordination, which the patient pages get from their layout route.
                  <ProtectedRoute accessor={PRODUCT_ACCESSORS.careCoordinationEnabled}>
                    <AcceptedPlansPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'denial-list',
                element: (
                  <ProtectedRoute
                    accessor={[
                      PRODUCT_ACCESSORS.inboundReferralsEnabled,
                      PERMISSIONS.denialListView,
                      PERMISSIONS.denialListItemView,
                    ]}>
                    <DenialListPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'high-cost-medications',
                element: (
                  <ProtectedRoute
                    accessor={[
                      PRODUCT_ACCESSORS.inboundReferralsEnabled,
                      PERMISSIONS.highCostMedicationListView,
                      PERMISSIONS.highCostMedicationListItemView,
                    ]}>
                    <HighCostMedsPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'referral-sources',
                element: (
                  // The flag as well as product access — hiding the nav entry is not enough on its own,
                  // the URL is guessable. ProtectedRoute checks `flag` before `accessor`.
                  <ProtectedRoute
                    flag={Flags.InboundReferrals}
                    accessor={[PRODUCT_ACCESSORS.inboundReferralsEnabled, PERMISSIONS.referralSourceView]}>
                    <ReferralSourcesPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'preferred-providers',
                element: (
                  <ProtectedRoute
                    flag={Flags.PreferredProviders}
                    accessor={[PRODUCT_ACCESSORS.preferredProvidersEnabled, PERMISSIONS.preferredProviderView]}>
                    <PreferredProvidersPage />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: '/configuration/facility-network-status',
            element: (
              <ProtectedRoute
                flag={Flags.FacilityInOutOfNetworkDesignation}
                accessor={[PRODUCT_ACCESSORS.inboundReferralsEnabled, PERMISSIONS.groupPayerNetworkStatusView]}>
                <Outlet />
              </ProtectedRoute>
            ),
            children: [
              { index: true, element: <FacilityNetworkStatusPage /> },
              { path: 'payers/:stediPayerId', element: <NetworkStatusPage kind='payer' /> },
              { path: 'facilities/:groupId', element: <NetworkStatusPage kind='facility' /> },
            ],
          },
          {
            path: '/summarizations',
            element: (
              <ProtectedRoute accessor={PRODUCT_ACCESSORS.olioSummarizationEnabled}>
                <Outlet />
              </ProtectedRoute>
            ),
            children: [
              {
                index: true,
                element: <SummarizationsListPage />,
              },
              {
                path: 'new',
                element: <NewSummarizationPage />,
              },
              {
                path: ':summarizationId',
                element: <ViewSummarizationPage />,
              },
            ],
          },
          {
            // The product gate sits per-child, not on the subtree: deep links to a run must
            // scope the acting client (WorkflowScopeGate) BEFORE the gate reads it, or a
            // cross-client user bounces to the default page before the page mounts (OLIO-2960).
            path: INBOUND_REFERRALS_ROUTES.BASE,
            children: [
              // Tasks is ONE page with a tab parameter, and therefore ONE route object: the tab
              // segment is optional rather than a second entry. Two sibling routes rendering the
              // same element are still two route ids to react-router, so moving between them
              // REMOUNTS the page — which would tear down ManualLaunchProvider on every tab
              // click, closing the user's portal tab and re-locking every card they'd unlocked.
              //
              // Static segments outrank the `:workflowId` sibling in react-router's ranking, so
              // `/inbound-referrals/tasks` can never be read as a run id.
              {
                // Wrapped in a `tasks` parent so `/inbound-referrals/tasks` is a matched ancestor
                // of every tab (useMatches surfaces it), which keeps the Tasks nav item highlighted
                // on the Portal Scans tab. The tab stays ONE `:tab?` route inside, so switching tabs
                // is a param change on the same route id — a re-render, NOT a remount — preserving
                // the no-remount guarantee above (ManualLaunchProvider survives the tab switch).
                path: 'tasks',
                children: [
                  {
                    // A deep-linked task rides `?task=<workflowId>` rather than a path segment —
                    // a sibling route would remount the page (see above). The gate scopes the
                    // acting client for that id before the product gate reads it, like the
                    // `:workflowId` route's WorkflowScopeGate.
                    path: ':tab?',
                    element: (
                      <TasksDeepLinkScopeGate>
                        <ProtectedRoute
                          accessor={PRODUCT_ACCESSORS.connectedPortalsEnabled}
                          flag={Flags.InboundReferrals}
                          redirectTo={INBOUND_REFERRALS_ROUTES.BASE}>
                          <TasksPage />
                        </ProtectedRoute>
                      </TasksDeepLinkScopeGate>
                    ),
                  },
                ],
              },
              {
                index: true,
                element: (
                  <ProtectedRoute
                    accessor={[PRODUCT_ACCESSORS.inboundReferralsEnabled, PERMISSIONS.workflowInboundReferralView]}
                    flag={Flags.InboundReferrals}>
                    <InboundReferralsListPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: ':workflowId',
                element: (
                  <WorkflowScopeGate fallbackPath={INBOUND_REFERRALS_ROUTES.BASE}>
                    <ProtectedRoute
                      accessor={[PRODUCT_ACCESSORS.inboundReferralsEnabled, PERMISSIONS.workflowInboundReferralView]}
                      flag={Flags.InboundReferrals}>
                      <Outlet />
                    </ProtectedRoute>
                  </WorkflowScopeGate>
                ),
                children: [
                  {
                    index: true,
                    element: <InboundReferralPage />,
                  },
                  {
                    // The manual-entry form writes, so it needs edit on top of the view the
                    // parent already requires. A view-only user lands back on the list.
                    path: 'edit',
                    element: (
                      <ProtectedRoute accessor={PERMISSIONS.workflowInboundReferralEdit}>
                        <EditInboundReferralPage />
                      </ProtectedRoute>
                    ),
                  },
                ],
              },
            ],
          },
          {
            // Inbound Referrals insights lives outside the careCoordination guard so any
            // client with inbound-referrals product access can still reach it. Gated on the
            // operational inbound_referrals flag so it matches the nav — no showing Insights
            // for a feature the client can't use yet.
            path: '/insights/inbound-referrals',
            element: (
              <ProtectedRoute accessor={PRODUCT_ACCESSORS.inboundReferralsEnabled} flag={Flags.InboundReferrals}>
                <InboundReferralsInsightsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: DISCHARGE_CALENDAR_ROUTES.BASE,
            element: (
              <ProtectedRoute accessor={PRODUCT_ACCESSORS.dischargeCalendarEnabled}>
                <DischargeCalendarPage />
              </ProtectedRoute>
            ),
            children: [
              {
                path: DISCHARGE_CALENDAR_VIEWS.WEEK,
                element: <WeekView />,
              },
              {
                path: DISCHARGE_CALENDAR_VIEWS.TWO_WEEK,
                element: <TwoWeekView />,
              },
              {
                path: DISCHARGE_CALENDAR_VIEWS.MONTH,
                element: <MonthView />,
              },
              {
                path: DISCHARGE_CALENDAR_VIEWS.DAY,
                element: <DayView />,
              },
              {
                path: DISCHARGE_CALENDAR_VIEWS.LIST,
                element: <ListView />,
              },
            ],
          },
          {
            path: '/admin',
            element: <AdminGuard />,
            children: [
              { path: 'demo-env-setup', element: <DemoEnvSetup /> },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminClientView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'clients',
                    element: <Outlet />,
                    children: [
                      { index: true, element: <Clients /> },
                      { path: 'collaborations', element: <Clients /> },
                      { path: 'import-configurations', element: <Clients /> },
                      { path: 'import-configurations/new', element: <EditImportConfiguration /> },
                      { path: 'import-configurations/:id/edit', element: <EditImportConfiguration /> },
                      { path: 'new', element: <EditClient /> },
                      { path: ':id/edit', element: <EditClient /> },
                      { path: ':id/group-types/edit', element: <ConfigureCareOptions /> },
                      { path: ':id/product-access', element: <ProductAccess /> },
                      // Gated on its own admin permission ON TOP OF adminClientView, which the outlet
                      // above already requires: managing a client's referral sources is a separate
                      // grant from seeing the client, and needing both is what matches the API, where
                      // the controller authorizes the referral source policy and scopes the client
                      // through Admin::ClientPolicy::Scope.
                      {
                        path: ':id/referral-sources',
                        element: (
                          <ProtectedRoute accessor={PERMISSIONS.adminReferralSourceView}>
                            <AdminReferralSources />
                          </ProtectedRoute>
                        ),
                      },
                      { path: ':id/portal-automation', element: <PortalAutomationSettings /> },
                    ],
                  },
                  // Global per-portal-system collected-field config (OLIO-3074) — its
                  // admin surface rides the client permissions like the rest of the
                  // portal admin pages.
                  {
                    path: 'portal-systems',
                    element: <Outlet />,
                    children: [
                      { index: true, element: <PortalSystems /> },
                      { path: 'new', element: <EditPortalSystem /> },
                      { path: ':id/edit', element: <EditPortalSystem /> },
                      { path: ':id/problem-reports', element: <PortalProblemReports /> },
                      { path: ':id/problem-reports/:reportId', element: <PortalProblemReports /> },
                    ],
                  },
                ],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminUserView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'users',
                    element: <Outlet />,
                    children: [
                      { index: true, element: <Users /> },
                      { path: 'new', element: <EditUser /> },
                      { path: ':id/edit', element: <EditUser /> },
                      { path: ':id/preferences', element: <AdminUserPreferences /> },
                      { path: ':id/permissions', element: <AdminEditPermissions /> },
                    ],
                  },
                ],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminCoordinateRequestView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [{ path: 'coordination-requests', element: <CoordinationRequests /> }],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminQuestionTemplateView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'question-templates',
                    element: <Outlet />,
                    children: [
                      { index: true, loader: () => redirect('/admin/question-templates/progress') },
                      { path: 'progress', element: <QuestionTemplates /> },
                      { path: 'discharge', element: <QuestionTemplates /> },
                      { path: ':type/new', element: <EditQuestionTemplate /> },
                      { path: ':type/:id/edit', element: <EditQuestionTemplate /> },
                    ],
                  },
                ],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminDischargeReasonView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'discharge-reasons',
                    element: <Outlet />,
                    children: [
                      { index: true, element: <DischargeReasons /> },
                      { path: 'new', element: <EditDischargeReason /> },
                      { path: ':id/edit', element: <EditDischargeReason /> },
                    ],
                  },
                ],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminGroupView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'groups',
                    element: <Outlet />,
                    children: [
                      { index: true, element: <Groups /> },
                      { path: 'new', element: <EditGroup /> },
                      { path: ':id/edit', element: <EditGroup /> },
                    ],
                  },
                  {
                    path: 'locations',
                    element: <Outlet />,
                    children: [
                      { index: true, element: <Locations /> },
                      { path: 'new', element: <EditLocation /> },
                      { path: ':id/edit', element: <EditLocation /> },
                      {
                        path: ':id/qhin-configuration',
                        element: (
                          <ProtectedRoute accessor={PERMISSIONS.adminQhinConfigurationView}>
                            <QhinConfiguration />
                          </ProtectedRoute>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminLinkedRecordView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [{ path: 'shared-records', element: <LinkedRecords /> }],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminGroupTypeView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'group-types',
                    element: <Outlet />,
                    children: [
                      { index: true, element: <GroupTypes /> },
                      { path: 'new', element: <EditGroupType /> },
                      { path: ':id/edit', element: <EditGroupType /> },
                    ],
                  },
                ],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminAttrView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'attributes',
                    element: <Outlet />,
                    children: [
                      { index: true, element: <Attributes /> },
                      { path: 'values', element: <Attributes /> },
                      { path: 'new', element: <EditAttribute /> },
                      { path: ':id/edit', element: <EditAttribute /> },
                      { path: 'values/new', element: <EditAttributeValue /> },
                      { path: 'values/:id/edit', element: <EditAttributeValue /> },
                    ],
                  },
                ],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminWorkflowLookupView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'workflows',
                    children: [
                      { index: true, element: <Navigate replace to='/admin/workflows/lookups' /> },
                      {
                        path: 'lookups',
                        element: <Outlet />,
                        children: [
                          { index: true, element: <WorkflowLookups /> },
                          // A list's panel is linkable, and the id belongs in the path, so a link to
                          // one survives a refresh and can be pasted to someone else.
                          { path: ':lookupId', element: <WorkflowLookups /> },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                // Its own permission, and its own branch: the run console reads across every
                // client, which the lookups screens do not.
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminWorkflowRunView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'workflows/runs',
                    children: [
                      { index: true, element: <WorkflowRuns /> },
                      // Static before the :id sibling, the same ordering /inbound-referrals
                      // relies on, so /runs/health is the health tab and not a run called health.
                      { path: 'health', element: <RunHealth /> },
                      { path: ':id', element: <RunDetail /> },
                    ],
                  },
                ],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminAIPromptView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'ai-prompts',
                    element: <Outlet />,
                    children: [
                      { index: true, element: <AIPrompts /> },
                      { path: 'new', element: <EditAIPrompt /> },
                      { path: ':id/edit', element: <EditAIPrompt /> },
                    ],
                  },
                  {
                    path: 'workspace-prompts',
                    element: <Outlet />,
                    children: [
                      { index: true, element: <WorkspacePrompts /> },
                      { path: 'new', element: <EditWorkspacePrompt /> },
                      { path: ':id/edit', element: <EditWorkspacePrompt /> },
                    ],
                  },
                  { path: 'generated-summaries', element: <Summaries /> },
                ],
              },
              {
                element: (
                  <ProtectedRoute
                    accessor={[
                      PERMISSIONS.adminAIPromptView,
                      PERMISSIONS.adminAICriteriaView,
                      PERMISSIONS.adminDenialListView,
                      PERMISSIONS.adminHighCostMedicationListView,
                    ]}
                    strictMode={false}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'admissions',
                    children: [
                      {
                        index: true,
                        element: (
                          <PermissionRedirect
                            routes={[
                              { permission: PERMISSIONS.adminAIPromptView, path: '/admin/admissions/prompts' },
                              { permission: PERMISSIONS.adminAICriteriaView, path: '/admin/admissions/criteria' },
                              { permission: PERMISSIONS.adminDenialListView, path: '/admin/admissions/denial-lists' },
                              {
                                permission: PERMISSIONS.adminHighCostMedicationListView,
                                path: '/admin/admissions/high-cost-medication-lists',
                              },
                            ]}
                          />
                        ),
                      },
                      // The list (index) is visible under the broad admissions outlet, but the
                      // new/edit forms need the stricter single-permission guard, so they sit behind
                      // an inner outlet. Nesting keeps /admin/admissions a matched ancestor for the
                      // nav highlight after the flat-link useMatches flip.
                      {
                        path: 'prompts',
                        children: [
                          { index: true, element: <Admissions /> },
                          {
                            element: (
                              <ProtectedRoute accessor={PERMISSIONS.adminAIPromptView}>
                                <Outlet />
                              </ProtectedRoute>
                            ),
                            children: [
                              { path: 'new', element: <EditAdmissionPrompt /> },
                              { path: ':id/edit', element: <EditAdmissionPrompt /> },
                            ],
                          },
                        ],
                      },
                      {
                        path: 'criteria',
                        children: [
                          { index: true, element: <Admissions /> },
                          {
                            element: (
                              <ProtectedRoute accessor={PERMISSIONS.adminAICriteriaView}>
                                <Outlet />
                              </ProtectedRoute>
                            ),
                            children: [
                              { path: 'new', element: <EditAICriteriaSet /> },
                              { path: ':id/edit', element: <EditAICriteriaSet /> },
                            ],
                          },
                        ],
                      },
                      { path: 'denial-lists', element: <Admissions /> },
                      // One splat route: <Admissions /> self-routes off the pathname (the tab
                      // resolves by prefix, the linkable slideout opens from the list segment and
                      // picks its view off what follows), and nothing reads the id via useParams —
                      // the sub-paths (:listId, items/new, :itemId/edit, tiers) only need to resolve.
                      { path: 'high-cost-medication-lists/*', element: <Admissions /> },
                    ],
                  },
                ],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminFeatureFlagView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'feature-flags',
                    element: <Outlet />,
                    children: [
                      { index: true, element: <FeatureFlags /> },
                      { path: ':name/edit', element: <EditFeatureFlag /> },
                    ],
                  },
                ],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminImportedPatientView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'import-history',
                    element: <Outlet />,
                    children: [
                      { index: true, element: <ImportHistory /> },
                      { path: ':id/status-details', element: <ImportStatusDetails /> },
                    ],
                  },
                ],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminBulkImportView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    path: 'bulk-import',
                    element: <Outlet />,
                    children: [
                      { index: true, element: <BulkImportHistory /> },
                      { path: 'new', element: <BulkImportNew /> },
                      { path: ':id', element: <BulkImportDetail /> },
                    ],
                  },
                ],
              },
              {
                element: (
                  <ProtectedRoute accessor={PERMISSIONS.adminClassificationView}>
                    <Outlet />
                  </ProtectedRoute>
                ),
                children: [
                  { path: 'plans', element: <Classifications /> },
                  { path: 'plan-types', element: <Classifications /> },
                  { path: 'episode-types', element: <Classifications /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/',
    element: (
      <ThemeProvider>
        <Outlet />
      </ThemeProvider>
    ),
    children: [
      {
        path: '/logged-out',
        element: <LoggedOut />,
      },
      {
        path: '/error',
        element: <AuthError />,
      },
    ],
  },
  {
    path: '*',
    index: true,
    loader: async () => redirect('/patients'),
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
